import { Container, Box, Grid, Hidden } from "@mui/material"
import { makeStyles } from "makeStyles"
import { SearchState, connectStats } from "react-instantsearch-core"
import dynamic from "next/dynamic"
import ResultsHeader from "components/Algolia/Results/ResultsHeader"
import SubCategories from "containers/CategoryPage/ChildCategories"
import DesktopFilters from "components/Algolia/Filters/DesktopFilters"
import SelectedFilters from "components/Algolia/Filters/SelectedFilters"
const Pagination = dynamic(() => import("components/Algolia/Pagination"), {
	ssr: false,
})
import Results from "components/Algolia/Results/Results"
import Description from "containers/CategoryPage/Description"
import ExposedSearchResultsPageTitle from "./SearchResultsPageTitle"
import CategoryInformation from "containers/CategoryPage/CategoryInformation"

import useToggle from "@secureo/common/hooks/useToggle"

import { FilterConfig } from "@secureo/common/typings/Algolia"
import { ChildCategory } from "@secureo/common/typings/Category"

export type AlgoliaUIPageType = "category" | "brand" | "search" | "locking-systems"

const useStyles = makeStyles()({
	hideMobileContainer: {
		display: "block",
		minHeight: 420,
	},
	"@media only screen and (max-width: 600px)": {
		hideMobileContainer: {
			marginLeft: -4,
			marginRight: -4,
		},
	},
})

export const h1Regex = /<h1>(?<headline>.+)<\/h1>/

const AlgoliaSearchUI = ({
	nbHits: totalNumberOfResults,
	locale,
	indexName,
	filters,
	title,
	shortDescription = "",
	description,
	childCategories = [],
	pageType = "category",
	query = "",
	virtualRefinements = {},
}: Props & StatsProvided) => {
	const { classes } = useStyles()

	const [isListView, toggleIsListView] = useToggle(true)

	const shortDescriptionContainsH1 = h1Regex.test(shortDescription)
	const headline = shortDescriptionContainsH1
		? h1Regex.exec(shortDescription)?.groups?.headline
		: null

	const shortDescriptionWithoutH1 = shortDescription.replace(h1Regex, "")

	return (
		<Box pb={5} mb={5}>
			<Container>
				<Grid container spacing={2}>
					<Grid item xs={12}>
						{pageType === "search" ? (
							<ExposedSearchResultsPageTitle
								query={query}
								totalNumberOfResults={totalNumberOfResults}
							/>
						) : (
							<h1
								style={{ marginBottom: 0 }}
								dangerouslySetInnerHTML={{ __html: headline || title }}
							/>
						)}

						{childCategories.length > 0 && (
							<SubCategories childCategories={childCategories} />
						)}

						{shortDescription && (
							<CategoryInformation shortDescription={shortDescriptionWithoutH1} />
						)}
					</Grid>

					<Grid item xs={12}>
						<ResultsHeader
							indexName={indexName}
							totalNumberOfResults={totalNumberOfResults}
							toggleIsListView={toggleIsListView}
							filters={filters}
							virtualRefinements={virtualRefinements}
						/>
					</Grid>
					<Hidden mdDown>
						<Grid item xs={12} style={{ paddingTop: 0, paddingBottom: "1rem" }}>
							<DesktopFilters
								filters={filters}
								virtualRefinements={virtualRefinements}
							/>
							<SelectedFilters virtualRefinements={virtualRefinements} />
						</Grid>
					</Hidden>

					<Grid item xs={12}>
						<Box mt={0} mx={1} className={classes.hideMobileContainer}>
							<Results
								isListView={isListView}
								locale={locale}
								categoryName={title}
								pageType={pageType}
							/>
						</Box>
					</Grid>
				</Grid>

				<Pagination />

				<Description description={description} />
			</Container>
		</Box>
	)
}

interface StatsProvided {
	nbHits: number
	processingTimeMS: number
}

interface Props extends Partial<StatsProvided> {
	locale: string
	indexName: string
	filters: FilterConfig[]
	title: string
	shortDescription?: string
	description?: string
	childCategories?: ChildCategory[]
	pageType?: AlgoliaUIPageType
	query?: string
	virtualRefinements?: SearchState
}

const ExposedAlgoliaSearchUI: React.ComponentClass<Props> = connectStats(AlgoliaSearchUI) as any

export default ExposedAlgoliaSearchUI
